import {useEffect, useMemo} from "react";
import {s} from "./style";
import NotFoundPage from "@pages/not-found";

// link.ridefload.com/{bikeId} 딥링크 착지 페이지.
// App Links가 정상이어도 카카오톡·삼성 노트 등 인앱 브라우저는 링크를 브라우저(크롬)로 열어
// 앱으로 튕기지 않는다(시스템 인텐트 미경유). 이 경우 이 페이지가 떠서 앱으로 유도한다.
// - Android: intent:// 로 앱 직접 실행(미설치 시 browser_fallback_url→Play).
// - iOS: 커스텀 스킴이 없어 베스트에포트(universal link 재시도 + App Store 폴백).
// owner.ridefload.com 등 다른 호스트에서는 렌더하지 않고 기존 NotFound 를 유지한다.

const LINK_HOST = "link.ridefload.com";
const ANDROID_PACKAGE = "com.balkensharing";
const PLAY_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
const APPSTORE_URL = "https://apps.apple.com/kr/app/id6450990490";

type Platform = "android" | "ios" | "other";

function detectPlatform(ua: string): Platform {
    if (/android/i.test(ua)) return "android";
    if (/iphone|ipad|ipod/i.test(ua)) return "ios";
    // iPadOS 13+ 는 Mac 처럼 보고되므로 터치 지원 Mac 을 iOS 로 취급.
    if (/macintosh/i.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1) return "ios";
    return "other";
}

function OpenAppPage() {
    const isLinkHost = typeof window !== "undefined" && window.location.hostname === LINK_HOST;
    const platform = useMemo(() => detectPlatform(typeof navigator !== "undefined" ? navigator.userAgent : ""), []);

    // 딥링크 경로(쿼리 포함). 예: "/20B00093" · "/coupon?x=1"
    const deepLinkPath = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
    const androidIntentUrl = useMemo(() => {
        const fallback = encodeURIComponent(PLAY_URL);
        return `intent://${LINK_HOST}${deepLinkPath}#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
    }, [deepLinkPath]);

    const openApp = () => {
        if (platform === "android") {
            window.location.href = androidIntentUrl;
        } else if (platform === "ios") {
            // iOS 는 커스텀 스킴이 없고, 같은 도메인으로 이동해도 universal link 가 재발화하지 않아
            // 앱을 직접 열 수 없다(특히 인앱 브라우저). 베스트에포트로 App Store 로 유도한다.
            window.location.href = APPSTORE_URL;
        } else {
            window.location.href = PLAY_URL;
        }
    };

    // Android 는 진입 즉시 자동 시도(미설치 시 intent 의 fallback 으로 Play 이동).
    // iOS 자동 이동은 앱 미설치 시 오작동(빈 페이지/스토어 강제 이동) 위험이 커 버튼으로만 처리.
    useEffect(() => {
        if (isLinkHost && platform === "android") {
            window.location.href = androidIntentUrl;
        }
    }, [isLinkHost, platform, androidIntentUrl]);

    // link.ridefload.com 이 아닌 호스트(owner 등)에서는 기존 404 유지.
    if (!isLinkHost) return <NotFoundPage/>;

    return (
        <s.Layout>
            <s.Card>
                <s.Title>플로드 앱에서 열기</s.Title>
                <s.Desc>
                    {"앱이 설치되어 있으면 자동으로 열립니다.\n열리지 않으면 아래 버튼을 눌러주세요."}
                </s.Desc>
                <s.PrimaryButton onClick={openApp}>앱에서 열기</s.PrimaryButton>
                <s.StoreRow>
                    <s.StoreLink href={PLAY_URL} target="_blank" rel="noreferrer">Google Play</s.StoreLink>
                    <s.StoreLink href={APPSTORE_URL} target="_blank" rel="noreferrer">App Store</s.StoreLink>
                </s.StoreRow>
            </s.Card>
        </s.Layout>
    );
}

export default OpenAppPage;
